import { useContext, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { AppContext } from '../context/AppProvider';
import { AuthContext } from '../context/AuthProvider';
import Grid from '@mui/material/Grid';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Divider from '@mui/material/Divider';
import CheckIcon from '@mui/icons-material/Check';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { cloneDeep, toNumber } from "lodash";
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { fetchWithInterceptor } from '../utils/fetchWithInterceptor';
import DownloadIcon from '@mui/icons-material/GetApp';

const PAGE_SIZE = 100;
const ROW_HEIGHT = 52;

const markupTypeMap = {
  P: "Percentage",
  A: "Absolute value",
  D: "Not defined/Delete"
};

const usePriceTypeMap = {
  A: "Average cost",
  F: "Latest fixed supplier cost",
  L: "Latest supplier cost",
  V: "Calculated value"
};

const statusMap = {
  pending: { label: 'Pending', color: 'warning' },
  applied: { label: 'Applied', color: 'success' },
  voided: { label: 'Voided', color: 'error' },
  selected: { label: 'Appoved', color: 'info' }
};

const columns = [
  { field: 'sku', headerName: 'SKU', width: 110 },
  {
    field: 'productName',
    headerName: 'Product Name',
    width: 200,
    renderCell: (params) => (
      <Tooltip title={params.value}>
        <span>{params.value}</span>
      </Tooltip>
    )
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: (params) => {
      const status = statusMap[params.value] || { label: params.value, color: 'default' };

      return (
        <Chip
          label={status.label}
          size="small"
          variant="filled"
          color={status.color} 
        />
      );
    }
  },
  { field: 'latestCost', headerName: 'Latest Cost', width: 100 },
  { field: 'fixedCost', headerName: 'Fixed Cost', width: 100 },
  { field: 'newCost', headerName: 'AGLC New Cost', width: 100 },
  {
    field: 'costChangeDirection',
    headerName: 'Cost Change Direction',
    width: 120,
    renderCell: (params) => {
      if (params.value === "I") {
        return <ArrowUpwardIcon color="error" />; 
      }
      return <ArrowDownwardIcon color="success" />;
    },
  },
  { field: 'costChangeAmount', headerName: 'Cost Change Amount', width: 120 },
  {
    field: 'costChangePercentage',
    headerName: 'Cost Change Percentage',
    width: 120,
    valueGetter: (value) => value ? `${value}%` : value
  },
  {
    field: 'markupType',
    headerName: 'Markup Type',
    width: 120,
    valueGetter: (value) => markupTypeMap[value] || value
  },
  {
    field: 'usePriceType',
    headerName: 'Use Price Type',
    width: 180,
    valueGetter: (value) => usePriceTypeMap[value] || value
  },
  {
    field: 'markupValue',
    headerName: 'Markup Value',
    width: 120,
    valueGetter: (value, row) => row.markupType === 'P' ? `${value}%` : value
  },
  {
    field: 'suggestedPrice',
    headerName: 'Suggested Price',
    width: 120,
    valueGetter: (value) => {
      return value !== null && value !== undefined ? `${Number(value).toFixed(2)}` : '';
    }
  }
];

const defaultFilter = {
  sku: "",
  name: "",
  status: "",
  costChangeDirection: "",
  usePriceType: "",
  percentageFrom: "",
  percentageTo: "",
  markupType: "",
};

const defaultOrder = [
  { field: 'costChangeDirection', sort: "asc" },
  { field: 'status', sort: "asc" },  
  { field: 'productName', sort: "asc" }   
];

export default function Cin7CostDetailsDialog({ open, onClose, id, status }) {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);
  const [selectionModel, setSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sortModel, setSortModel] = useState(defaultOrder);
  const [filter, setFilter] = useState(cloneDeep(defaultFilter));
  const [pendingStatus, setPendingStatus] = useState(null);

  let active = useRef(false);
  
  const handleReset = () => {
    setFilter(cloneDeep(defaultFilter));
    setSortModel(cloneDeep(defaultOrder));
  }

  const getOrderBy = () => {
    return JSON.stringify(sortModel.map(item => ({
      [item.field]: item.sort
    })));
  };

  const handleStatusUpdate = async (status) => {
    try {
      const urlMap = {
        selected: "/api/inventory-management/mark-as-selected",
        voided: "/api/inventory-management/mark-as-voided",
        update: "/api/inventory-management/update-costs",
      };
      
      const selectedIds = selectionModel.ids.values();

      let payload;

      if (status === "update") {
        payload = { fileLogId: id };
      } else {
        payload = { fileLogId: id, itemIds: Array.from(selectedIds) };
      }

      const response = await fetchWithInterceptor(urlMap[status], {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (response.ok) {
        appContext.showMessage("Operation successful", "success");
        setSelectionModel({ type: 'include', ids: new Set() });
        loadProducts(false);

        if (status === "update") {
          appContext.showMessage("We will send you an email when the cost update is complete", "success");
          onClose(true)
        }
      } else {
        appContext.showMessage(result.error || "Error updating", "error");
      }
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error updating", "error");
    }
  };

  const openConfirmationDialog = (status) => {
    setPendingStatus(status);
    setConfirmOpen(true);
  };

  const loadProducts = async (reset) => {
    if (!id) return;
    setIsLoading(true);
    let filters = {
      id,
      limit: PAGE_SIZE,
      offset: reset ? 0 : paginationModel.page * PAGE_SIZE,
      orderBy: getOrderBy()
    };

    if (filter.sku) {
      filters.sku = filter.sku;
    }
    if (filter.name) {
      filters.name = filter.name;
    }
    if (filter.status) {
      filters.status = filter.status;
    }
    if (filter.costChangeDirection) {
      filters.costChangeDirection = filter.costChangeDirection;
    }
    if (filter.usePriceType) {
      filters.usePriceType = filter.usePriceType;
    }
    if (filter.percentageFrom) {
      filters.percentageFrom = filter.percentageFrom;
    }
    if (filter.percentageTo) {
      filters.percentageTo = filter.percentageTo;
    }
    if (filter.markupType) {
      filters.markupType = filter.markupType;
    }
    const params = new URLSearchParams(filters);
    const response = await fetchWithInterceptor(`/api/inventory-management/cin7-cost-products?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': authContext.getToken(),
      },
    });
    if (response.ok) {
      if (!active.current) return;
      const data = await response.json();
      setProducts(data.products || []);
      if (reset) {
        setPaginationModel({ ...paginationModel, page: 0 });
      }
      setRowCount(data.total || 0);
    } else {
      appContext.showMessage('Error fetching Cin7 products', 'error');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  useEffect(() => {
    loadProducts(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

 useEffect(() => {
  if (!id) return;
  loadProducts(true);
  //eslint-disable-next-line react-hooks/exhaustive-deps
}, [id, sortModel, filter]);

  const isValidPercentage = () => {
    try {
      if (!filter.percentageFrom || !filter.percentageTo) {
        return true;
      }
      return toNumber(filter.percentageFrom) <= toNumber(filter.percentageTo);
    } catch (error) {
      return false;
    }
  }

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingStatus(null);
  };

  const handleConfirm = () => {
    handleStatusUpdate(pendingStatus);
    setConfirmOpen(false);
    setPendingStatus(null);
  };

  const handleExport = async () => {
    try {
      setIsDownloading(true);

      const params = new URLSearchParams({
        id,
        sku: filter.sku || "",
        name: filter.name || "",
        status: filter.status || "",
        costChangeDirection: filter.costChangeDirection || "",
        usePriceType: filter.usePriceType || "",
        percentageFrom: filter.percentageFrom || "",
        percentageTo: filter.percentageTo || "",
        markupType: filter.markupType || "",
      });

      const response = await fetchWithInterceptor(
        `/api/inventory-management/cin7-cost-products/export?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Export failed.");
      }

      appContext.showMessage("You will receive the CSV file in your email inbox shortly", "success");
    } catch (err) {
      console.error("Export error:", err);
      appContext.showMessage("Error executing export", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
            Product details (Cin7 Cost Upload)
          </Typography>
        </Toolbar>
      </AppBar>
      <div style={{ padding: 16 }}>
        <Typography variant="h6">Search Filters</Typography>
        <br />

        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField
                label="SKU"
                variant="outlined"
                value={filter.sku}
                onChange={(e) =>
                  setFilter({ ...filter, sku: e.target.value })
                }
                color="primary"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField
                label="Product Name"
                variant="outlined"
                value={filter.name}
                onChange={(e) =>
                  setFilter({ ...filter, name: e.target.value })
                }
                color="primary"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField
                select
                label="Status"
                variant="outlined"
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                color="primary"
                fullWidth
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="selected">Approved</MenuItem>
                <MenuItem value="voided">Discarded</MenuItem>
                <MenuItem value="applied">Updated in Cin7</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField
                select
                label="Cost Change Direction"
                variant="outlined"
                value={filter.costChangeDirection}
                onChange={(e) => setFilter({ ...filter, costChangeDirection: e.target.value })}
                color="primary"
                fullWidth
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="I">Increment</MenuItem>
                <MenuItem value="D">Decrement</MenuItem>
              </TextField>
            </Grid>
   
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField
                label="Percentage from"
                variant="outlined"
                value={filter.percentageFrom}
                onChange={(e) =>
                  setFilter({ ...filter, percentageFrom: e.target.value })
                }
                type="number"
                color="primary"
                fullWidth
              />
            </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField
                label="Percentage to"
                variant="outlined"
                value={filter.percentageTo}
                onChange={(e) =>
                  setFilter({ ...filter, percentageTo: e.target.value })
                }
                error={!isValidPercentage()}
                type="number"
                color="primary"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField
                select
                label="Use Price Type"
                variant="outlined"
                value={filter.usePriceType}
                onChange={(e) => setFilter({ ...filter, usePriceType: e.target.value })}
                color="primary"
                fullWidth
              >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value="A">Average cost</MenuItem>
              <MenuItem value="F">Latest fixed supplier cost</MenuItem>
              <MenuItem value="L">Latest supplier cost</MenuItem>
              <MenuItem value="V">Calculated value</MenuItem>
            </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField
                select
                label="Markup Type"
                variant="outlined"
                value={filter.markupType}
                onChange={(e) => setFilter({ ...filter, markupType: e.target.value })}
                color="primary"
                fullWidth
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="P">Percentage</MenuItem>
                <MenuItem value="A">Absolute value</MenuItem>
                <MenuItem value="D">Not defined / Delete</MenuItem>
              </TextField>
            </Grid>
           <Grid size={{ xs: 6, sm: 12, md: 12 }} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button color="primary" onClick={handleReset}>Reset filters</Button>
          </Grid>
        </Grid>
      </div>

      <Divider />

      <Grid container spacing={2} style={{ padding: 16 }}>
        <Grid>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={handleExport}
            startIcon={<DownloadIcon />}
            disabled={isDownloading || rowCount === 0}
          >
            Export
          </Button>
        </Grid>

        <Grid item sx={{ flexGrow: 1 }} />

        <Grid>
          <Button
            variant="contained"
            color="primary"
            disabled={selectionModel.ids.size === 0 || status !== "pending"}
            onClick={() => openConfirmationDialog("voided")}
          >
            Discard Suggestion
          </Button>
        </Grid>
        <Grid>
          <Button
            variant="contained"
            color="primary"
            disabled={selectionModel.ids.size === 0 || status !== "pending"}
            onClick={() => openConfirmationDialog("selected")}
          >
            Approve Suggestion
          </Button>
        </Grid>
        <Grid>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckIcon />}
            onClick={() => openConfirmationDialog("update")}
            disabled={status !== "pending" && status !== "inProgress" && status !== "failed"}
          >
            Send to Cin7
          </Button>
        </Grid>
      </Grid>

      <div style={{ padding: 16 }}>
        <DataGrid
          rows={products}
          columns={columns}
          pageSize={PAGE_SIZE}
          pageSizeOptions={[PAGE_SIZE]}
          disableColumnFilter
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          rowCount={rowCount}
          loading={isLoading}
          rowHeight={ROW_HEIGHT}
          getRowId={(row) => row.id}
          isRowSelectable={(params) => status === "pending" ? true : false}
          checkboxSelection={status === "pending"}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => setSelectionModel(model)}
          sortingMode="server"
          onSortModelChange={(model) => setSortModel(model)}
          disableRowSelectionOnClick
          sx={{
            height: ROW_HEIGHT * PAGE_SIZE + 120,
            border: 'none',
            '& .MuiDataGrid-menuIcon': { display: 'none' },
            '& .MuiDataGrid-columnHeaderTitle': {
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: 'rgba(58, 53, 65, 0.87)',
              whiteSpace: 'wrap',
              lineHeight: 1,
            },
            '& .MuiDataGrid-cell--withRenderer': {
              display: 'flex',
              textOverflow: 'unset',
            },
            '& .MuiDataGrid-cell--withRenderer span': {
              overflow: 'hidden',
              textWrap: 'nowrap',
              textOverflow: 'ellipsis',
              display: 'block',
            },
          }}
        />
      </div>
      <Dialog open={confirmOpen} onClose={handleCancel}>
        <DialogTitle>Confirm price modification</DialogTitle>
        <DialogContent>
          {pendingStatus === "selected" && "Are you sure you want to approve the selected products?"}
          {pendingStatus === "voided" && "Are you sure you want to discard the selected products?"}
          {pendingStatus === "update" && "Are you sure you want to send the selected products to Cin7 for cost update?"}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
